import React, { useMemo, useState } from 'react';
import { Select } from 'antd';
import { debounce } from 'lodash';
import { useQuery } from '@apollo/client';
// API
import { GET_STORES_BY_NAME_AND_EMAIL } from 'api/store/queries';
import {
  GetStoresByNameAndEmail,
  GetStoresByNameAndEmailVariables,
} from 'api/store/types/GetStoresByNameAndEmail';

const { Option, OptGroup } = Select;

type Store = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  storeDetails?: { storeName?: string | null } | null;
};

type Props = {
  value?: string[];
  onChange?: (ids: string[]) => void;
  placeholder?: string;
};

const getStoreLabel = (store: Store): string => {
  const fullName = [store.firstName, store.lastName].filter(Boolean).join(' ');
  const name = store.storeDetails?.storeName || fullName || 'Unnamed';
  return store.email ? `${name} (${store.email})` : name;
};

/**
 * Multi-select that searches users by store name / email and submits userId[].
 * Mirrors the AddTalentModal search pattern but adds multi-select support and
 * remembers labels for previously-picked ids so they keep showing as chips when
 * the search term changes.
 */
const UserSelect: React.FC<Props> = ({
  value,
  onChange,
  placeholder = 'Search by store name or email',
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [resolvedLabels, setResolvedLabels] = useState<Record<string, string>>(
    {}
  );

  const { data, refetch } = useQuery<
    GetStoresByNameAndEmail,
    GetStoresByNameAndEmailVariables
  >(GET_STORES_BY_NAME_AND_EMAIL, {
    variables: { searchTerm: '' },
    fetchPolicy: 'cache-and-network',
  });

  const handleSearch = useMemo(
    () =>
      debounce((v: string) => {
        setSearchTerm(v);
        refetch({ searchTerm: v });
      }, 400),
    [refetch]
  );

  const nameStores = (data?.nameStores.entities || []) as Store[];
  const emailStores = (data?.emailStores.entities || []) as Store[];
  const seenIds = new Set(nameStores.map((s) => s.id));
  const dedupedEmailStores = emailStores.filter((s) => !seenIds.has(s.id));

  const handleChange = (ids: string[]): void => {
    const next = { ...resolvedLabels };
    [...nameStores, ...emailStores].forEach((s) => {
      if (ids.includes(s.id)) {
        next[s.id] = getStoreLabel(s);
      }
    });
    setResolvedLabels(next);
    onChange?.(ids);
  };

  const visibleIds = new Set([
    ...nameStores.map((s) => s.id),
    ...dedupedEmailStores.map((s) => s.id),
  ]);
  // Selected ids that aren't in the current results — keep them as Options so
  // antd can render the chip with a proper label instead of the raw userId.
  const orphanSelected = (value || []).filter((id) => !visibleIds.has(id));

  return (
    <Select
      mode="multiple"
      value={value}
      onChange={handleChange}
      showSearch
      filterOption={false}
      onSearch={handleSearch}
      placeholder={placeholder}
      notFoundContent={searchTerm ? 'No results' : 'Start typing to search'}
    >
      {orphanSelected.length > 0 && (
        <OptGroup label="Currently selected">
          {orphanSelected.map((id) => (
            <Option key={`sel_${id}`} value={id}>
              {resolvedLabels[id] || id}
            </Option>
          ))}
        </OptGroup>
      )}
      {nameStores.length > 0 && (
        <OptGroup label="By name">
          {nameStores.map((store) => (
            <Option key={`name_${store.id}`} value={store.id}>
              {getStoreLabel(store)}
            </Option>
          ))}
        </OptGroup>
      )}
      {dedupedEmailStores.length > 0 && (
        <OptGroup label="By email">
          {dedupedEmailStores.map((store) => (
            <Option key={`email_${store.id}`} value={store.id}>
              {getStoreLabel(store)}
            </Option>
          ))}
        </OptGroup>
      )}
    </Select>
  );
};

export default UserSelect;
